/**
 * PDF Generator for Modular Closets
 * Generates PDFs using jsPDF and html2canvas without external API calls
 */

class PDFGenerator {
  constructor() {
    this.hidePrices = false;
    this.canvasOverlay = null;
    this.overlayText = null;
    this.logGroup = "PDFGenerator";
  }

  /**
   * Log only errors
   */
  log(level, message, ...args) {
    if (level === "error") {
      console.error(`[PDFGenerator] ❌ ${message}`, ...args);
    }
  }

  /**
   * Main function to generate and download PDF
   * @param {Object} options - Configuration options
   * @param {boolean} options.hidePrices - Whether to hide prices in PDF
   * @param {string} options.closetName - Name of the closet
   * @param {Array} options.towersArray - Array of tower objects
   * @param {Function} options.captureScreenshot - Function to capture screenshots
   * @param {Function} options.getTotalVariants - Function to get total variants
   */
  async generatePDF(options = {}) {
    this.log("info", "Starting PDF generation process", { options });

    try {
      // Initialize options
      this.hidePrices = options.hidePrices || false;
      this.closetName = options.closetName || "";
      this.towersArray = options.towersArray || [];
      this.captureScreenshot = options.captureScreenshot;
      this.getTotalVariants = options.getTotalVariants;
      this.discountAmount = options.discountAmount || 0;
      this.discountPercent = options.discountPercent || 0;
      this.discountedTotal = options.discountedTotal || null;
      this.collectionType = options.collectionType || "VERRA";

      this.log("debug", "Options initialized", {
        hidePrices: this.hidePrices,
        closetName: this.closetName,
        towersCount: this.towersArray.length,
        hasCaptureScreenshot: !!this.captureScreenshot,
        hasGetTotalVariants: !!this.getTotalVariants,
      });

      // Check if we have any towers to work with
      if (this.towersArray.length === 0) {
        this.log("warn", "No towers found - generating empty PDF");
        // We'll still generate a PDF but with empty content
      }

      this.showLoadingOverlay();

      const closet = await this.prepareClosetData();
      this.log("debug", "Closet data prepared", {
        towers: closet.towers.length,
        parts: closet.parts.length,
        hasImages: !!(closet.image_url_door_open || closet.image_url_door_closed),
      });

      const pdfContainer = this.createPDFContainer();
      this.log("debug", "PDF container created");

      const htmlContent = this.generateHTMLContent(closet);
      this.log("debug", "HTML content generated", { contentLength: htmlContent.length });

      pdfContainer.innerHTML = htmlContent;
      document.body.appendChild(pdfContainer);
      this.log("debug", "PDF container added to DOM");

      await this.generateAndDownloadPDF(pdfContainer);
      this.log("success", "PDF generated and downloaded successfully");

      this.cleanup(pdfContainer);
    } catch (error) {
      this.log("error", "PDF generation failed", error);
      this.showError("Error generating PDF. Please try again.");
    }
  }

  /**
   * Show loading overlay
   */
  showLoadingOverlay() {
    this.log("debug", "Showing loading overlay");

    this.canvasOverlay = document.querySelector(".mc-loading-overlay");
    this.overlayText = this.canvasOverlay && this.canvasOverlay.querySelector ? this.canvasOverlay.querySelector(".mc-loading") : null;

    if (this.overlayText) {
      this.overlayText.innerText = "Generating PDF...";
      this.log("debug", "Loading text updated");
    } else {
      this.log("warn", "Loading text element not found");
    }

    if (this.canvasOverlay) {
      this.canvasOverlay.style["z-index"] = "2";
      this.canvasOverlay.style.display = "flex";
      this.log("debug", "Loading overlay displayed");
    } else {
      this.log("warn", "Loading overlay element not found");
    }
  }

  /**
   * Prepare closet data from towersArray
   */
  async prepareClosetData() {
    this.log("debug", "Preparing closet data from towersArray", {
      towersCount: this.towersArray.length,
    });

    let hasDoors = false;
    let variants = [];
    let parts = [];

    // Use the collection type passed from the Liquid template
    const closetType = this.collectionType;

    const closet = {
      color: document.querySelector(`input[name="color"]:checked`)?.value || "White",
      image_url_door_open: "",
      image_url_door_closed: "",
      closet_name: this.closetName || "",
      collection: closetType,
      total_price: this.towersArray[0]?.totalPrice / 100 || 0,
      towers: [],
    };

    this.log("debug", "Base closet object created", {
      color: closet.color,
      closetName: closet.closet_name,
      totalPrice: closet.total_price,
    });

    this.towersArray.forEach((t, index) => {
      this.log("debug", `Processing tower ${index + 1}`, {
        width: t.width.selectedValue,
        top: t.topOptions.selectedValue,
        bottom: t.bottomOptions.selectedValue,
        door: t.doorOptions.selectedValue,
      });

      if (t.doorOptions.selectedValue !== "none") {
        hasDoors = true;
      }

      closet.towers.push({
        width: parseFloat(t.width.selectedValue),
        top: this.transformTowerOption(t.topOptions.selectedValue),
        bottom: this.transformTowerOption(t.bottomOptions.selectedValue),
        door: this.transformDoorOption(t.doorOptions.selectedValue),
      });

      // Collect variants from all tower options
      this.collectVariants(t.variants, variants);
      this.collectVariants(t.topOptions.variants, variants);
      this.collectVariants(t.bottomOptions.variants, variants);
      this.collectVariants(t.doorOptions.variants, variants);
      // Collect hardware variants (knobs and pulls)
      this.collectVariants(t.doorHardwareOptions?.variants || [], variants);
      this.collectVariants(t.drawerHardwareOptions?.variants || [], variants);
    });

    this.log("debug", "Towers processed", {
      towersCount: closet.towers.length,
      hasDoors: hasDoors,
      variantsCount: variants.length,
    });

    // Capture screenshots
    if (this.captureScreenshot) {
      this.log("debug", "Capturing screenshots", { hasDoors });
      try {
        // First, open doors if they exist, then capture
        if (hasDoors && typeof setDoorsState === "function") {
          setDoorsState("Open Doors");
          // Small delay to ensure doors are fully opened
          await new Promise(resolve => setTimeout(resolve, 1000));
        }
        closet.image_url_door_open = this.captureScreenshot(false).image;
        this.log("debug", "Door open screenshot captured", {
          hasImage: !!closet.image_url_door_open,
          imageLength: closet.image_url_door_open?.length || 0,
        });

        if (hasDoors && typeof setDoorsState === "function") {
          // Then close doors and capture closed state
          setDoorsState("Close Doors");
          // Small delay to ensure doors are fully closed
          await new Promise(resolve => setTimeout(resolve, 1000));
        }
        closet.image_url_door_closed = hasDoors ? this.captureScreenshot(false).image : null;
        this.log("debug", "Door closed screenshot captured", {
          hasImage: !!closet.image_url_door_closed,
          imageLength: closet.image_url_door_closed?.length || 0,
        });
      } catch (error) {
        this.log("warn", "Screenshot capture failed", error);
      }
    } else {
      this.log("warn", "No captureScreenshot function provided");
    }

    // Process parts
    if (this.getTotalVariants) {
      this.log("debug", "Processing variants and parts");
      try {
        const variantIdQuantity = this.getTotalVariants(variants);
        this.log("debug", "Variant quantities calculated", {
          variantQuantities: variantIdQuantity.length,
        });

        variantIdQuantity.forEach(viq => {
          const variant = variants.find(v => v.id === viq.id);
          if (variant) {
            parts.push({
              ...variant,
              qty: viq.quantity,
            });
          }
        });
        this.log("debug", "Parts processed", { partsCount: parts.length });
      } catch (error) {
        this.log("warn", "Parts processing failed", error);
      }
    } else {
      this.log("warn", "No getTotalVariants function provided");
    }

    closet.parts = parts;

    if (this.hidePrices) {
      closet.total_price = null;
      this.log("debug", "Prices hidden as requested");
    }

    this.log("debug", "Closet data preparation complete", {
      finalTowersCount: closet.towers.length,
      finalPartsCount: closet.parts.length,
      hasDoorOpenImage: !!closet.image_url_door_open,
      hasDoorClosedImage: !!closet.image_url_door_closed,
      hidePrices: this.hidePrices,
    });

    return closet;
  }

  /**
   * Transform tower option text
   */
  transformTowerOption(option) {
    if (option === "short-hanging") {
      return "Hanging";
    }
    return option
      .split("-")
      .map(word => {
        return ["of", "over"].includes(word)
          ? word
          : word.charAt(0).toUpperCase() + word.slice(1).toLowerCase();
      })
      .join(" ");
  }

  /**
   * Transform door option text
   */
  transformDoorOption(doorValue) {
    if (doorValue === "none") return "";
    return (
      doorValue.split("-")[0].charAt(0).toUpperCase() +
      doorValue.split("-")[0].slice(1).toLowerCase()
    );
  }

  /**
   * Collect variants from array
   */
  collectVariants(variantsArray, variants) {
    variantsArray.forEach(v =>
      variants.push({
        id: v.id,
        title: v.productTitle?.replace("(1)", "").replace("(2)", "") || "",
        variant_title: v.description?.replace("(1)", "").replace("(2)", "") || "",
        name: v.name?.replace("(1)", "").replace("(2)", "") || "",
        price: v.price / 100,
        image_url: v.image || "",
        sku: v.sku || "",
      })
    );
  }

  /**
   * Create PDF container element
   */
  createPDFContainer() {
    const pdfContainer = document.createElement("div");
    pdfContainer.className = "mc-pdf-generator-container";
    pdfContainer.style.position = "absolute";
    pdfContainer.style.left = "-9999px";
    pdfContainer.style.top = "0";
    pdfContainer.style.width = "8.5in";
    pdfContainer.style.backgroundColor = "white";
    pdfContainer.style.fontFamily = "Favorit";
    pdfContainer.style.fontSize = "12px";
    pdfContainer.style.padding = "10px";
    return pdfContainer;
  }

  /**
   * Check if order qualifies for free shipping
   */
  qualifiesForFreeShipping(totalPrice) {
    const freeShippingThreshold = 99999999999; // You can adjust this threshold
    return totalPrice > freeShippingThreshold;
  }

  /**
   * Generate HTML content for PDF matching simplified design
   */
  generateHTMLContent(closet) {
    const freeShippingText = this.qualifiesForFreeShipping(closet.total_price)
      ? '<div class="mc-pdf-small-shipping-header-div">Your order qualifies for free shipping!</div>'
      : "";

    const priceSection =
      closet.total_price && !this.hidePrices
        ? `
        <div class="mc-pdf-price-container">
          <br />
          ${
            this.discountPercent > 0
              ? `
          <div class="mc-pdf-d-flex mc-pdf-justify-content-between">
            <div>Subtotal</div>
            <div>$${closet.total_price.toFixed(2)}</div>
          </div>
          <div class="mc-pdf-d-flex mc-pdf-justify-content-between">
            <div>Discount (${this.discountPercent}%)</div>
            <div>-$${this.discountAmount.toFixed(2)}</div>
          </div>
          `
              : ""
          }
          <div class="mc-pdf-d-flex mc-pdf-justify-content-between">
            <div class="mc-pdf-strong">TOTAL</div>
            <div class="mc-pdf-strong">$${(this.discountedTotal || closet.total_price).toFixed(
              2
            )}</div>
          </div>
        </div>
      `
        : "";

    return `
      <style>
        @font-face {
          font-family: "PDF-Favorit";
          src: url("https://favorit-font.s3.us-east-1.amazonaws.com/FavoritMedium.otf") format("opentype");
          font-weight: 500;
          font-style: normal;
        }
        @font-face {
          font-family: "PDF-Favorit-Bold";
          src: url("https://favorit-font.s3.us-east-1.amazonaws.com/FavoritBold.otf") format("opentype");
          font-weight: 900;
          font-style: normal;
        }
        @font-face {
          font-family: "PDF-Favorit-Light";
          src: url("https://favorit-font.s3.us-east-1.amazonaws.com/FavoritLight.otf") format("opentype");
          font-weight: 500;
          font-style: normal;
        }
        .mc-pdf-mt-5 { margin-top: 2px; }
        .mc-pdf-tower-title { font-size: 15px; padding-bottom: 10px; }
        .mc-pdf-gray-text { font-weight: 100; color: #f4f4f4; margin-left: 2px; font-size: 10px; }
        .mc-pdf-tower-details { font-size: 10px; line-height: 1.2; }
        .mc-pdf-d-flex { display: flex; }
        .mc-pdf-justify-content-between { justify-content: space-between; }
        .mc-pdf-main-container { display: flex; }
        .mc-pdf-main-container-item { border-radius: 10px; flex-grow: 1; }
        .mc-pdf-left-container { flex-basis: 65%; margin-right: 10px; }
        .mc-pdf-right-container { margin-left: 10px; flex-basis: 35%; background-color: #faf9f8; padding: 10px 15px; }
        .mc-pdf-items-heading { margin: 5px 0px; font-size: 15px; }
        .mc-pdf-item-card { display: flex; align-items: center; margin-bottom: 1px; padding: 1px; border: 1px solid #d7d7d7; border-radius: 5px; width: 100%; font-size: 8px; background-color: #ffffff; line-height: 1.2; }
        .mc-pdf-item-card img { max-width: 45px; max-height: 45px; object-fit: cover; }
        .mc-pdf-item-card-content { display: flex; flex-direction: column; justify-content: space-between; padding: 1px; flex-grow: 1; }
        .mc-pdf-item-card-header { display: flex; flex-direction: column; }
        .mc-pdf-item-card-title { font-weight: bold; font-size: 8px; }
        .mc-pdf-item-card-subtitle { color: #555; }
        .mc-pdf-item-card-sku { color: #555; font-size: 7px; }
        .mc-pdf-item-card-price { font-weight: bold; color: #000; }
        .mc-pdf-item-card-footer { display: flex; justify-content: space-between; margin-top: 3px; }
        .mc-pdf-item-card-quantity { color: #555; }
        .mc-pdf-header-div { background: #dbf0ff; border-radius: 10px; padding: 8px; font-weight: bold; margin: 0; }
        .mc-pdf-description-div { background: #2b87c9; border-radius: 10px; width: auto; padding: 12px; margin: 20px 0; color: white; }
        .mc-pdf-tg td { font-size: 14px; overflow: hidden; padding: 10px 5px; word-break: normal; }
        .mc-pdf-tg hr { width: 100px; margin-left: 0; margin-top: 0; border-style: solid; border-width: 0.5px; border-color: white; }
        .mc-pdf-image-container img { width: 100%; height: 340px; border-radius: 10px; background: #faf9f8; object-fit: cover; }
        .mc-pdf-small-shipping-header-div { background: #dbf0ff; border-radius: 10px; padding: 3%; margin-bottom: 3%; text-align: left; font-size: 10px; }
        .mc-pdf-strong { font-family: "PDF-Favorit-Bold"; font-weight: 900; display: inline-block; }
        .mc-pdf-price-container { font-size: 10px;}
        .mc-pdf-towers-bottom { 
          margin-top: 20px; 
          width: 100%; 
        }
        .mc-pdf-components-container { page-break-inside: auto; }
        .mc-pdf-pdf-body { 
          margin: 0 5px; 
          font-family: "PDF-Favorit"; 
          font-weight: 500; 
          font-style: normal; 
          font-stretch: normal; 
          line-height: 1.2; 
        }
        .mc-pdf-main-container { page-break-inside: avoid; }
        .mc-pdf-towers-bottom { page-break-inside: avoid; }
        .mc-pdf-item-card { page-break-inside: avoid; margin-bottom: 5px; }
        .mc-pdf-right-container { page-break-inside: auto; }
      </style>
      
      <div class="mc-pdf-pdf-body">
        <div class="mc-pdf-main-container mc-pdf-d-flex">
        <div class="mc-pdf-main-container-item mc-pdf-left-container">
            <div class="mc-pdf-logo-container">
                    <svg width="178" height="46" viewBox="0 0 178 46" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M50.3043 26.9498V20.5431C50.3043 20.3209 50.3043 20.1668 50.155 19.996C50.0632 19.8708 49.9461 19.7677 49.8118 19.6939C49.6776 19.62 49.5294 19.5772 49.3775 19.5683C48.9632 19.5683 48.6161 19.9099 47.5561 20.5958V26.9523H45.5556V18.1503H47.2973L47.4143 19.1072C47.943 18.3892 48.8039 17.8947 49.6537 17.8947C50.6129 17.8947 51.4079 18.4084 51.9043 19.2433C52.4168 18.4919 53.3437 17.8947 54.3539 17.8947C55.9426 17.8947 57.0686 19.1791 57.0686 20.8668V26.9498H55.0382V20.5431C55.0382 20.3209 55.0382 20.1668 54.8889 19.996C54.7968 19.8707 54.6795 19.7676 54.545 19.6937C54.4106 19.6199 54.2622 19.5771 54.1101 19.5683C53.697 19.5683 53.3499 19.9099 52.2899 20.5958V26.9523L50.3043 26.9498Z" fill="#1C1C1E"/>
                <path d="M58.518 23.2764V21.8237C58.518 19.7057 60.2896 17.8947 62.4071 17.8947C64.5245 17.8947 66.3136 19.7057 66.3136 21.8237V23.2764C66.3136 25.447 64.5258 27.2067 62.4071 27.2067C60.2883 27.2067 58.518 25.447 58.518 23.2764ZM64.0455 24.6776C64.2446 24.4041 64.2608 24.1639 64.2608 23.8402V21.2431C64.2608 20.9182 64.2446 20.696 64.0455 20.4224C63.8581 20.1578 63.6128 19.9425 63.3296 19.7942C63.0464 19.6458 62.7333 19.5684 62.4158 19.5684C62.0982 19.5684 61.7851 19.6458 61.5019 19.7942C61.2187 19.9425 60.9735 20.1578 60.786 20.4224C60.5707 20.696 60.5708 20.9182 60.5708 21.2431V23.8402C60.5708 24.1652 60.5707 24.4041 60.786 24.6776C60.9735 24.9423 61.2187 25.1575 61.5019 25.3059C61.7851 25.4543 62.0982 25.5316 62.4158 25.5316C62.7333 25.5316 63.0464 25.4543 63.3296 25.3059C63.6128 25.1575 63.8581 24.9423 64.0455 24.6776Z" fill="#1C1C1E"/>
                <path d="M67.7741 23.8749V21.2431C67.7741 19.2947 69.114 17.8947 70.9516 17.8947C71.9469 17.8947 72.7219 18.3378 73.3154 19.0044V14.8866H75.3184V26.9498H73.548L73.4485 25.9595C72.8202 26.7622 71.9556 27.2067 70.8508 27.2067C69.2459 27.2067 67.7741 25.8914 67.7741 23.8749ZM73.3179 20.714C71.8249 19.7391 71.6794 19.5683 71.0996 19.5683C70.8638 19.5777 70.6345 19.6504 70.4342 19.7792C70.2339 19.908 70.0696 20.0886 69.9575 20.303C69.8399 20.4973 69.7822 20.7239 69.7921 20.9529V24.1999C69.7736 24.3991 69.808 24.5998 69.8916 24.7804C70.0339 25.0139 70.2321 25.2057 70.4669 25.3373C70.7018 25.4688 70.9655 25.5358 71.2327 25.5318C71.6632 25.5318 71.9108 25.3263 73.3179 24.4554V20.714Z" fill="#1C1C1E"/>
                <path d="M84.2001 18.1503V26.9498H82.4459L82.3128 25.9762C82.0186 26.3521 81.6484 26.657 81.228 26.8695C80.8076 27.0821 80.3473 27.1971 79.8793 27.2066C78.3404 27.2066 77.0328 25.9762 77.0328 24.1819V18.1451H79.0358V24.3655C79.0189 24.5555 79.0593 24.7462 79.1515 24.9114C79.4165 25.3044 79.7972 25.5446 80.1779 25.5446C80.525 25.5446 80.7403 25.271 82.1971 24.3989V18.1503H84.2001Z" fill="#1C1C1E"/>
                <path d="M85.9556 14.8866H87.9586V26.9498H85.9556V14.8866Z" fill="#1C1C1E"/>
                <path d="M89.3831 24.3874C89.374 23.7098 89.61 23.0531 90.0449 22.5455C91.0054 21.4692 92.3627 21.518 94.6954 21.333V20.7474C94.6954 20.4571 94.6631 20.3376 94.5635 20.1835C94.2488 19.6877 93.7026 19.5683 93.3057 19.5683C92.8591 19.5683 92.6762 19.6363 90.4592 20.9015L89.5647 20.0294C90.3112 18.5947 91.7494 17.8947 93.1403 17.8947C94.1849 17.8735 95.1956 18.2782 95.9532 19.0211C96.6648 19.7391 96.6822 20.5252 96.6822 21.3626V26.9498H94.9106L94.7949 25.9929C94.4837 26.373 94.096 26.6785 93.6588 26.8881C93.2215 27.0977 92.7453 27.2064 92.2632 27.2066C90.6247 27.2066 89.3831 26.1303 89.3831 24.3874ZM94.6954 22.8461L92.7073 23.0593C92.2607 23.1107 91.6971 23.2134 91.4632 23.6398C91.416 23.777 91.3936 23.922 91.3973 24.0676V24.4259C91.3973 24.6481 91.3973 24.8703 91.4632 24.9898C91.6623 25.3828 92.2246 25.5369 92.6551 25.5369C93.0196 25.5369 93.3505 25.3147 94.6904 24.5287L94.6954 22.8461Z" fill="#1C1C1E"/>
                <path d="M98.2946 18.1503H100.048L100.163 19.1585C100.693 18.3879 101.438 17.8947 102.316 17.8947C103.392 17.8947 104.036 18.578 104.385 19.3075L103.557 20.2644C102.447 19.6659 102.447 19.6145 102.15 19.6145C101.786 19.6145 101.67 19.7853 100.296 20.7088V26.9446H98.2934L98.2946 18.1503Z" fill="#1C1C1E"/>
                <path d="M113.937 24.4166L114.848 25.3388C114.503 25.9628 114.005 26.4816 113.403 26.8425C112.801 27.2034 112.118 27.3936 111.422 27.3938C109.307 27.3938 107.533 25.6342 107.533 23.4635V22.007C107.533 19.871 109.304 18.0767 111.422 18.0767C112.846 18.0767 114.12 18.8808 114.848 20.1626L113.905 21.1529C111.985 19.9391 111.87 19.7516 111.417 19.7516C111.102 19.7576 110.793 19.8377 110.513 19.9857C110.232 20.1337 109.988 20.3458 109.8 20.6057C109.584 20.8793 109.584 21.1015 109.584 21.4265V24.0402C109.584 24.3652 109.584 24.6041 109.8 24.8777C109.989 25.1357 110.233 25.3454 110.514 25.4905C110.794 25.6357 111.103 25.7126 111.417 25.7151C111.87 25.7151 112.018 25.561 113.937 24.4166Z" fill="#1C1C1E"/>
                <path d="M115.901 15.4026H117.902V27.1331H115.901V15.4026Z" fill="#1C1C1E"/>
                <path d="M119.354 23.4597V22.007C119.354 19.889 121.125 18.0767 123.243 18.0767C125.362 18.0767 127.15 19.889 127.15 22.007V23.4597C127.15 25.6303 125.362 27.39 123.243 27.39C121.125 27.39 119.354 25.6252 119.354 23.4597ZM124.882 24.861C125.081 24.5874 125.097 24.3472 125.097 24.0235V21.4265C125.097 21.1015 125.081 20.8793 124.882 20.6057C124.694 20.3411 124.449 20.1259 124.166 19.9775C123.882 19.8291 123.569 19.7518 123.251 19.7518C122.934 19.7518 122.621 19.8291 122.337 19.9775C122.054 20.1259 121.809 20.3411 121.621 20.6057C121.406 20.8793 121.406 21.1015 121.406 21.4265V24.0235C121.406 24.3485 121.406 24.5874 121.621 24.861C121.809 25.1256 122.054 25.3409 122.337 25.4892C122.621 25.6376 122.934 25.7149 123.251 25.7149C123.569 25.7149 123.882 25.6376 124.166 25.4892C124.449 25.3409 124.694 25.1256 124.882 24.861Z" fill="#1C1C1E"/>
                <path d="M133.482 22.0417C135.153 22.332 135.534 23.2208 135.534 24.3318C135.534 26.0914 133.78 27.39 131.761 27.39C131.02 27.4019 130.291 27.2087 129.647 26.8306C129.004 26.4524 128.471 25.9031 128.103 25.2399L129.046 24.2997C130.912 25.4274 131.396 25.7125 131.761 25.7125C132.39 25.7125 133.005 25.5417 133.367 25.1834C133.445 25.1183 133.507 25.0345 133.548 24.9392C133.588 24.8439 133.605 24.7399 133.598 24.6362V24.3793C133.598 24.0557 133.465 23.9529 132.936 23.8501L130.437 23.389C128.683 23.0641 128.368 22.2267 128.368 21.1156C128.368 19.356 130.056 18.0742 132.009 18.0742C133.465 18.0742 134.591 18.8448 135.302 20.074L134.388 21.0013C132.646 19.9417 132.419 19.7542 132.006 19.7542C131.376 19.7542 130.796 19.925 130.449 20.2679C130.371 20.333 130.308 20.4167 130.268 20.5121C130.228 20.6074 130.211 20.7114 130.218 20.8151V21.072C130.218 21.3789 130.35 21.4984 130.864 21.5857L133.482 22.0417Z" fill="#1C1C1E"/>
                <path d="M143.189 24.4166L144.049 25.3221C143.708 25.9511 143.211 26.4745 142.608 26.8382C142.006 27.2018 141.32 27.3923 140.622 27.39C138.507 27.39 136.733 25.6303 136.733 23.4597V22.007C136.733 19.889 138.505 18.0767 140.622 18.0767C142.691 18.0767 144.397 19.8377 144.397 21.8542V23.425H138.77V24.0749C138.77 24.3999 138.77 24.6388 138.984 24.9124C139.176 25.1655 139.421 25.3695 139.702 25.5088C139.982 25.648 140.29 25.7186 140.601 25.7151C141.07 25.7151 141.401 25.5096 143.189 24.4166ZM142.46 21.4265C142.46 21.1015 142.444 20.8793 142.245 20.6057C142.057 20.3411 141.812 20.1259 141.528 19.9775C141.245 19.8291 140.932 19.7518 140.614 19.7518C140.297 19.7518 139.983 19.8291 139.7 19.9775C139.417 20.1259 139.171 20.3411 138.984 20.6057C138.77 20.8793 138.77 21.1015 138.77 21.4265V21.9223H142.46V21.4265Z" fill="#1C1C1E"/>
                <path d="M150.859 25.2258C150.097 25.6111 149.932 25.7151 149.739 25.7151C149.342 25.7151 148.978 25.4929 148.697 25.1166C148.589 24.9572 148.536 24.7648 148.547 24.5707V19.9905H151.615L152.237 18.3336H148.549V15.4026H146.546V18.3336H144.839V19.9905H146.543V24.1944C146.543 26.0401 147.868 27.39 149.722 27.39C150.526 27.3988 151.304 27.099 151.906 26.5487L150.859 25.2258Z" fill="#1C1C1E"/>
                <path d="M157.726 22.0417C159.398 22.332 159.779 23.2208 159.779 24.3318C159.779 26.0914 158.024 27.39 156.005 27.39C155.264 27.4018 154.535 27.2085 153.891 26.8304C153.248 26.4522 152.715 25.903 152.347 25.2399L153.291 24.2997C155.157 25.4274 155.641 25.7125 156.005 25.7125C156.634 25.7125 157.249 25.5417 157.611 25.1834C157.689 25.1183 157.751 25.0345 157.792 24.9392C157.832 24.8439 157.849 24.7399 157.842 24.6362V24.3793C157.842 24.0557 157.71 23.9529 157.18 23.8501L154.681 23.389C152.927 23.0641 152.612 22.2267 152.612 21.1156C152.612 19.356 154.3 18.0742 156.253 18.0742C157.71 18.0742 158.835 18.8448 159.546 20.074L158.637 21.0013C156.895 19.9417 156.668 19.7542 156.253 19.7542C155.624 19.7542 155.045 19.925 154.697 20.2679C154.618 20.333 154.556 20.4167 154.516 20.5121C154.476 20.6074 154.458 20.7114 154.466 20.8151V21.072C154.466 21.3789 154.599 21.4984 155.111 21.5857L157.726 22.0417Z" fill="#1C1C1E"/>
                <path d="M166.613 23.0538H162.667V27.128H166.613V23.0538Z" fill="#F27662"/>
                <path d="M32.0232 36.936H10.9259V15.9896C10.9278 14.1541 11.6348 12.3943 12.892 11.0964C14.1492 9.79851 15.8538 9.06853 17.6317 9.06665H38.122V30.6396L32.0232 36.936ZM12.3359 35.4777H31.4386L36.7095 30.0361V10.5258H17.6308C16.2274 10.5274 14.882 11.1037 13.8897 12.1282C12.8974 13.1527 12.3392 14.5417 12.3376 15.9905L12.3359 35.4777Z" fill="#1C1C1E"/>
                <path d="M17.3248 15.3224H19.8128V18.1377H17.0858V15.5691C17.0858 15.5037 17.111 15.4409 17.1558 15.3947C17.2006 15.3484 17.2614 15.3224 17.3248 15.3224Z" fill="#1C1C1E"/>
                <path d="M25.8884 15.3224H23.1614V18.1377H25.8884V15.3224Z" fill="#1C1C1E"/>
                <path d="M31.9631 15.3224H29.2361V18.1377H31.9631V15.3224Z" fill="#1C1C1E"/>
                <path d="M19.8128 21.5948H17.0858V24.4101H19.8128V21.5948Z" fill="#1C1C1E"/>
                <path d="M25.8884 21.5948H23.1614V24.4101H25.8884V21.5948Z" fill="#1C1C1E"/>
                <path d="M31.9631 21.5948H29.2361V24.4101H31.9631V21.5948Z" fill="#1C1C1E"/>
                <path d="M19.8128 27.8662H17.0858V30.6816H19.8128V27.8662Z" fill="#1C1C1E"/>
                <path d="M25.8884 27.8662H23.1614V30.6816H25.8884V27.8662Z" fill="#1C1C1E"/>
                <path d="M29.2361 30.6816V27.8662H31.9631L29.2361 30.6816Z" fill="#1C1C1E"/>
                </svg>
            </div>
          <div class="mc-pdf-header-div">
            <div style="color: gray; font-size: 8px;">YOUR ${closet.collection} CLOSET DESIGN</div>
            ${closet.closet_name || `${closet.collection} Wardrobe`}
          </div>
          <br>
          <div class="mc-pdf-image-container">
            ${
              closet.image_url_door_open
                ? `<img src="${closet.image_url_door_open}" alt="Doors Open">`
                : ""
            }
          </div>
          <br>
          ${
            closet.image_url_door_closed
              ? `
            <div class="mc-pdf-image-container">
              <img src="${closet.image_url_door_closed}" alt="Doors Closed">
            </div>
            <br>
          `
              : ""
          }
        </div>
        
        <div class="mc-pdf-main-container-item mc-pdf-right-container">
          ${freeShippingText}
          ${this.generateComponentsSection(closet.parts)}
          <hr style="width: 100%; margin-right: 0; margin-top: 12px;">
          ${priceSection}
        </div>
      </div>

      <div class="mc-pdf-towers-bottom">
        <div class="mc-pdf-description-div">
          <table class="mc-pdf-tg">
            <thead>
              <tr>
                ${closet.towers
                  .map((tower, index) => this.generateTowerHTML(tower, index))
                  .join("")}
              </tr>
            </thead>
            </table>
          <div style="font-size: 10px; padding-top: 10px; padding-left: 5px;">
            <span class="mc-pdf-strong">COLOR</span>
            <span class="mc-pdf-gray-text">${closet.color}</span>
          </div>
        </div>
      </div>
      </div>
    `;
  }

  /**
   * Generate tower HTML for the description section
   */
  generateTowerHTML(tower, index) {
    return `
    <div class="mc-pdf-tower">
      <td>
        <hr>
        <div class="mc-pdf-tower-title">Tower ${index + 1}</div>
        <div class="mc-pdf-tower-details">
          <div class="mc-pdf-mt-5">
            <span class="mc-pdf-strong">Width</span> 
            <span class="mc-pdf-gray-text">${tower.width}″</span>
                </div>
          <div class="mc-pdf-mt-5">
            <span class="mc-pdf-strong">Top</span> 
            <span class="mc-pdf-gray-text">${tower.top}</span>
                </div>
          <div class="mc-pdf-mt-5">
            <span class="mc-pdf-strong">Bottom</span>
            <span class="mc-pdf-gray-text">${tower.bottom}</span>
                </div>
          <div class="mc-pdf-mt-5">
            <span class="mc-pdf-strong">Doors</span>
            <span class="mc-pdf-gray-text">${tower.door || "No Doors"}</span>
              </div>
            </div>
      </td>
      </div>`;
  }

  /**
   * Generate part HTML for the items list
   */
  generatePartHTML(part) {
    const formattedPrice =
      part.price && !this.hidePrices ? `$${parseFloat(part.price).toFixed(2)}` : "";
    return `
      <div class="mc-pdf-item-card">
        <img src="${part.image_url || ""}" alt="${part.title}">
        <div class="mc-pdf-item-card-content">
          <div class="mc-pdf-item-card-header">
            <div class="mc-pdf-item-card-title">${part.title}</div>
            <div class="mc-pdf-item-card-subtitle">${part.variant_title}</div>
            <div class="mc-pdf-item-card-sku">${part.sku}</div>
          </div>
          <div class="mc-pdf-item-card-footer">
            ${formattedPrice ? `<div class="mc-pdf-item-card-price">${formattedPrice}</div>` : ""}
            <div class="mc-pdf-item-card-quantity">Qty: ${part.qty}</div>
          </div>
        </div>
      </div>`;
  }

  /**
   * Generate components section with pagination support
   */
  generateComponentsSection(parts) {
    if (!parts || parts.length === 0) {
      return '<div class="mc-pdf-items-heading">No components</div>';
    }

    const componentsHTML = parts.map(part => this.generatePartHTML(part)).join("");

    return `
      <div class="mc-pdf-items-heading">Component list</div>
      <div class="mc-pdf-components-container">
        ${componentsHTML}
      </div>
    `;
  }

  /**
   * Generate and download PDF using html2canvas and jsPDF with proper pagination
   */
  async generateAndDownloadPDF(pdfContainer) {
    this.log("debug", "Starting PDF generation with html2canvas and jsPDF");

    return new Promise((resolve, reject) => {
      if (typeof html2canvas === "undefined" || typeof window.jspdf === "undefined") {
        this.log("error", "Required libraries not loaded", {
          html2canvas: typeof html2canvas,
          jsPDF: typeof window.jspdf,
        });
        reject(new Error("Required libraries (html2canvas, jsPDF) not loaded"));
        return;
      }

      this.log("debug", "Libraries verified, starting html2canvas conversion");

      html2canvas(pdfContainer, {
        scale: 2,
        useCORS: true,
        allowTaint: true,
        backgroundColor: "#ffffff",
        logging: false,
      })
        .then(canvas => {
          this.log("debug", "html2canvas conversion complete", {
            canvasWidth: canvas.width,
            canvasHeight: canvas.height,
          });

          const { jsPDF } = window.jspdf;
          const imgData = canvas.toDataURL("image/jpeg", 0.95);
          const pdf = new jsPDF({
            orientation: "portrait",
            unit: "in",
            format: "letter",
          });

          this.log("debug", "jsPDF instance created, processing image data");

          const imgWidth = 8.5;
          const pageHeight = 11;
          const imgHeight = (canvas.height * imgWidth) / canvas.width;

          this.log("debug", "PDF dimensions calculated", {
            imgWidth,
            imgHeight,
            pageHeight,
          });

          // Add the first page
          pdf.addImage(imgData, "JPEG", 0, 0, imgWidth, imgHeight);

          // Handle multiple pages if content is taller than one page
          if (imgHeight > pageHeight) {
            let heightLeft = imgHeight - pageHeight;
            let position = -pageHeight;

            while (heightLeft > 0) {
              pdf.addPage();
              pdf.addImage(imgData, "JPEG", 0, position, imgWidth, imgHeight);
              heightLeft -= pageHeight;
              position -= pageHeight;
            }
          }

          const pageCount = Math.ceil(imgHeight / pageHeight);
          this.log("debug", "PDF pages created", { pageCount });

          // Download the PDF
          const fileName = `${this.closetName || "ClosetDesign"}.pdf`;
          pdf.save(fileName);
          this.log("success", "PDF saved successfully", { fileName });
          resolve();
        })
        .catch(error => {
          this.log("error", "PDF generation failed", error);
          reject(error);
        });
    });
  }

  /**
   * Clean up temporary elements
   */
  cleanup(pdfContainer) {
    this.log("debug", "Starting cleanup process");

    if (pdfContainer && pdfContainer.parentNode) {
      document.body.removeChild(pdfContainer);
      this.log("debug", "PDF container removed from DOM");
    } else {
      this.log("warn", "PDF container not found or already removed");
    }

    var overlays = document.querySelectorAll(".mc-loading-overlay");
    for (var i = 0; i < overlays.length; i++) {
      overlays[i].style.display = "none";
      overlays[i].style.zIndex = "";
    }
    if (overlays.length > 0) {
      this.log("debug", "Loading overlay hidden");
    } else if (!this.canvasOverlay) {
      this.log("warn", "Loading overlay not found");
    }

    this.canvasOverlay = null;
    this.log("debug", "Cleanup completed");
  }

  /**
   * Show error message
   */
  showError(message) {
    this.log("error", "Showing error to user", { message });
    this.cleanup();
    alert(message);
  }
}

// Global function for backward compatibility
window.downloadAsPDFHTMLTOJS = function (collectionType) {
  const hidePrices = document.querySelector(".mc-hide-prices")?.querySelector("input").checked;
  const generator = new PDFGenerator();

  // Access variables from the global scope and UI
  const globalClosetName = typeof closetName !== "undefined" ? closetName : "";
  const globalTowersArray = typeof towersArray !== "undefined" ? towersArray : [];

  // Try to get closet name from UI input if global variable is empty
  const uiClosetName = document.querySelector(".mc-popup-input")?.value || "";
  const finalClosetName = globalClosetName || uiClosetName || "Closet Design";

  const options = {
    hidePrices: hidePrices,
    closetName: finalClosetName,
    towersArray: globalTowersArray,
    captureScreenshot: window.document?.App?.captureScreenshot,
    getTotalVariants: window.getTotalVariants,
    discountAmount: typeof discountAmount !== "undefined" ? discountAmount : 0,
    discountPercent: typeof discountPercent !== "undefined" ? discountPercent : 0,
    discountedTotal: typeof discountedTotal !== "undefined" ? discountedTotal : null,
    collectionType: collectionType || "VERRA",
  };

  console.log("PDF Generation Options:", {
    hidePrices: options.hidePrices,
    closetName: options.closetName,
    towersCount: options.towersArray.length,
    hasCaptureScreenshot: !!options.captureScreenshot,
    hasGetTotalVariants: !!options.getTotalVariants,
    discountAmount: options.discountAmount,
    discountPercent: options.discountPercent,
    discountedTotal: options.discountedTotal,
    collectionType: collectionType,
  });

  generator.generatePDF(options);
};

// Export for module usage
if (typeof module !== "undefined" && module.exports) {
  module.exports = PDFGenerator;
}
